import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { images, site } from "../data/siteData";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { Stars } from "../components/ReviewCard";

const chapters = [
  {
    title: "Our Philosophy",
    text: "Great food is a language everyone speaks. We built Mango around a simple belief — that a table set with dishes from many corners of the world has a way of bringing people closer together.",
  },
  {
    title: "Our Food",
    text: "Ten cuisines, one kitchen, no shortcuts. From Punjabi classics simmered slow to fondues that arrive bubbling at the table, every plate is crafted to be generous, beautiful and full of flavour.",
  },
  {
    title: "Our Experience",
    text: "Premium, but never stiff. Warm lighting, comfortable seating and a team that treats every guest like a regular — whether it is a quick weekday lunch or a long family celebration.",
  },
];

const stats = [
  { value: site.rating, label: "Google Rating", stars: true },
  { value: site.reviewCount, label: "Google Reviews" },
  { value: "10", label: "World Cuisines" },
  { value: "100%", label: "Vegetarian-Friendly" },
];

export default function About() {
  useEffect(() => {
    document.title = "About Us | Mango – World Cuisine Restaurant, Bhavnagar";
  }, []);

  return (
    <>
      <PageHero
        overline="Our Story"
        lines={["Food that brings", "the world closer."]}
        image={images.heroAbout}
        description="A premium yet comfortable world-cuisine restaurant in the heart of Bhavnagar."
      />

      <section data-testid="about-chapters" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading overline="Why We Exist" title="The Mango way" />
          <div className="mt-16 flex flex-col">
            {chapters.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 md:py-14 border-t border-line last:border-b">
                  <span className="md:col-span-2 font-display italic text-5xl md:text-6xl text-gold/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="md:col-span-4 font-display text-2xl md:text-3xl text-charcoal self-start">
                    {c.title}
                  </h3>
                  <p className="md:col-span-6 text-base md:text-lg leading-relaxed text-charcoal/70">
                    {c.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-why-guests" className="bg-forest-deep py-24 md:py-32 text-ivory">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeading
            dark
            overline="Why Guests Love Mango"
            title="Rated by the people who matter — our guests."
          />
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.1}>
                <div data-testid={`about-stat-${i}`} className="border-l border-gold/30 pl-6">
                  <p className="font-display text-4xl md:text-5xl text-gold">{s.value}</p>
                  {s.stars && <Stars size={14} className="mt-2" />}
                  <p className="mt-2 text-sm text-ivory/60">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-16 max-w-2xl font-display text-2xl md:text-3xl leading-snug text-ivory/90">
              &ldquo;Good food, great service, excellent family time and very
              accommodating staff.&rdquo;
            </p>
            <p className="mt-4 text-sm text-gold font-semibold">— Dhara Trivedi, Google Review</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem]">
              <img
                src={images.intro}
                alt="The warm, family-friendly dining room at Mango"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </Reveal>
          <div>
            <SectionHeading
              overline="Come Hungry"
              title="Every evening deserves a little Mango."
              description="Dine-in, takeaway or delivery — with vegan options, a kids' menu and room for the whole family."
            />
            <Reveal delay={0.15}>
              <Link
                to="/menu"
                data-testid="about-menu-button"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:bg-forest-deep hover:-translate-y-0.5"
              >
                Explore the Menu
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
